import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import useSeo from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

type SortKey = 'latest' | 'hot';

export default function Topics() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get('q') || '').trim();
  const [activeCat, setActiveCat] = useState('all');
  const [sort, setSort] = useState<SortKey>('latest');

  useSeo({
    title: '话题总览 - 美日文化冲突全部观察 | 美日吃瓜',
    description:
      '美日吃瓜话题总览，汇集争议言论、网络骂战、价值观碰撞、政治正确与翻车现场全部观察内容，支持按栏目筛选与热门排序。',
    keywords: '美日吃瓜话题总览,美日文化冲突,争议言论,网络骂战,翻车现场',
    path: '/topics',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '话题总览',
        url: 'https://dxvfa.cn/topics',
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: '美日吃瓜', url: 'https://dxvfa.cn' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dxvfa.cn/' },
          { '@type': 'ListItem', position: 2, name: '话题总览', item: 'https://dxvfa.cn/topics' },
        ],
      },
    ],
  });

  const list = useMemo(() => {
    let result = [...articles];
    if (activeCat !== 'all') {
      result = result.filter((item) => item.category === activeCat);
    }
    if (keyword) {
      const kw = keyword.toLowerCase();
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(kw) ||
          item.summary.toLowerCase().includes(kw) ||
          item.tags.some((tag) => tag.toLowerCase().includes(kw)),
      );
    }
    result.sort((a, b) => (sort === 'hot' ? b.views - a.views : a.date < b.date ? 1 : -1));
    return result;
  }, [activeCat, sort, keyword]);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '话题总览' }]} />
            <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              话题总览
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这里收纳了频道内全部观察文章。你可以按栏目筛选，也可以切换到「按热度」排序，看看大家都在围观什么。
              {keyword && (
                <span className="mt-2 block text-primary-600">
                  当前搜索关键词：「{keyword}」，共找到 {list.length} 条结果。
                </span>
              )}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className={`${cat.icon} text-base text-primary-500`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="mb-8 flex flex-col gap-5 border-b border-background-200 pb-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveCat('all')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCat === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:text-primary-600'
                }`}
              >
                全部栏目
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => setActiveCat(cat.slug)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-700 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setSort('latest')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  sort === 'latest' ? 'bg-background-50 text-primary-600' : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                最新发布
              </button>
              <button
                type="button"
                onClick={() => setSort('hot')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  sort === 'hot' ? 'bg-background-50 text-primary-600' : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                按热度
              </button>
            </div>
          </div>

          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 py-20 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">暂时没有匹配的内容</p>
              <p className="mt-2 text-sm text-foreground-500">换个栏目或关键词再试试。</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((item) => (
                <ArticleCard key={item.slug} article={item} />
              ))}
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}